import { FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import {
  Button,
  Spinner,
  Input,
} from '../components/design-system';
import { pickPhoto, ensureCameraPermission } from '../lib/device/camera';
import { AvatarCropModal } from '../components/profile/AvatarCropModal';
import { getCurrentPosition } from '../lib/device/geolocation';
import { useContentWidth, type ContentWidth } from '../hooks/useContentWidth';
import { TwoFactorSettings } from '../components/security/TwoFactorSettings';
import { SignInMethods } from '../components/security/SignInMethods';

export function Settings() {
  const { user, profile, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { width: contentWidth, setWidth: setContentWidth } = useContentWidth();
  const [fullName, setFullName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  // The picked photo, waiting for the traveler to preview, recenter and save it.
  const [pendingPhoto, setPendingPhoto] = useState<string | null>(null);

  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteSent, setDeleteSent] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (profile?.full_name) setFullName(profile.full_name);
    setAvatarUrl(profile?.avatar_url ?? null);
    if (profile?.latitude != null && profile?.longitude != null) {
      setCoords({ lat: profile.latitude, lng: profile.longitude });
    }
  }, [profile]);

  function flash(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError(null);
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ full_name: fullName.trim(), updated_at: new Date().toISOString() })
      .eq('id', user.id);
    setSaving(false);
    if (updateError) setError(updateError.message);
    else flash('Name updated');
  }

  async function handleAvatar() {
    if (!user || uploadingAvatar) return;
    setError(null);
    try {
      if (!(await ensureCameraPermission())) {
        setError('Camera/photo permission is required.');
        return;
      }
      const photo = await pickPhoto('prompt');
      if (photo) setPendingPhoto(photo.dataUrl);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not open your photo');
    }
  }

  /** Uploads the cropped square (always a JPEG, see AvatarCropModal) and saves its link. */
  async function saveAvatar(photo: Blob) {
    if (!user) return;
    setError(null);
    setUploadingAvatar(true);
    try {
      const path = `${user.id}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, photo, { upsert: true, contentType: 'image/jpeg' });
      if (uploadError) throw uploadError;

      const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
      const publicUrl = pub.publicUrl;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      setPendingPhoto(null);
      flash('Photo updated');
    } catch (err: unknown) {
      // Rethrown so the modal stays open with its own error and the traveler can retry.
      setError(err instanceof Error ? err.message : 'Could not update photo');
      throw err;
    } finally {
      setUploadingAvatar(false);
    }
  }

  async function handleLocation() {
    if (!user || locating) return;
    setError(null);
    setLocating(true);
    try {
      const pos = await getCurrentPosition();
      if (!pos) {
        setError('Location unavailable or permission denied.');
        return;
      }
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          latitude: pos.latitude,
          longitude: pos.longitude,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (updateError) throw updateError;
      setCoords({ lat: pos.latitude, lng: pos.longitude });
      flash('Location saved');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save location');
    } finally {
      setLocating(false);
    }
  }

  async function handleDeleteAccount() {
    if (!user || deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const { data, error } = await supabase.functions.invoke('request-account-deletion');
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setDeleteSent(true);
    } catch (err: unknown) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to request deletion');
    } finally {
      setDeleting(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  if (loading) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const initial = (fullName || user?.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-4 py-6 flex flex-col gap-6">
        <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Settings</h1>
        {/* Profile */}
        <section className="bg-app-card rounded-2xl border border-app-border shadow-sm p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-app-fg-2">Profile</h2>

          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full overflow-hidden avatar-host flex items-center justify-center text-cream text-xl font-semibold shrink-0">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  initial
                )}
              </div>
              {uploadingAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                  <Spinner size="sm" className="text-white" />
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleAvatar()}
              disabled={uploadingAvatar}
            >
              {avatarUrl ? 'Change photo' : 'Add photo'}
            </Button>
          </div>

          <form onSubmit={handleSave} className="flex flex-col gap-4">
            <Input
              label="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={saving}
              placeholder="Your name"
            />
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-app-fg-2">Email</label>
              <p className="text-sm text-app-muted">{user?.email}</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-app-fg-2">Role</label>
              <p className="text-sm text-app-muted capitalize">{profile?.role}</p>
            </div>
            {error && <p className="text-xs text-crimson">{error}</p>}
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm" disabled={saving || !fullName.trim()}>
                {saving ? <Spinner size="sm" className="text-ink" /> : 'Save'}
              </Button>
              {toast && <span className="text-xs text-accent font-medium">{toast}</span>}
            </div>
          </form>
        </section>

        {/* Location */}
        <section className="bg-app-card rounded-2xl border border-app-border shadow-sm p-5 flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-app-fg-2">Location</h2>
          <p className="text-xs text-app-muted leading-relaxed">
            Share your location to help Alma match you with nearby hosts. Optional —
            you can update or clear it anytime.
          </p>
          {coords && (
            <p className="text-xs text-app-fg-2">
              Saved: {coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}
            </p>
          )}
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleLocation()}
              disabled={locating}
            >
              {locating ? <Spinner size="sm" /> : coords ? 'Update location' : 'Use my location'}
            </Button>
          </div>
        </section>

        {/* Content width */}
        <section className="bg-app-card rounded-2xl border border-app-border shadow-sm p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-app-fg-2">Content width</h2>
              <p className="text-xs text-app-muted mt-0.5">Maximum width of the page content.</p>
            </div>
            <div className="flex bg-app-bg border border-app-border rounded-lg p-0.5 shrink-0">
              {(['narrow', 'medium', 'wide'] as ContentWidth[]).map((w) => (
                <button
                  key={w}
                  onClick={() => setContentWidth(w)}
                  className={`sk-hit px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${
                    contentWidth === w
                      ? 'bg-app-card text-app-fg shadow-sm border border-app-border'
                      : 'text-app-muted hover:text-app-fg'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-app-card rounded-2xl border border-app-border shadow-sm p-5 flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-app-fg-2">Sign-in methods</h2>
          <SignInMethods />
        </section>

        {/* Account */}
        <section className="bg-app-card rounded-2xl border border-app-border shadow-sm p-5 flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-app-fg-2">Two-factor authentication</h2>
          <TwoFactorSettings />
        </section>

        <section className="bg-app-card rounded-2xl border border-app-border shadow-sm p-5">
          <h2 className="text-sm font-semibold text-app-fg-2 mb-4">Account</h2>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => void handleSignOut()}>
              Sign out
            </Button>
            <button
              onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(''); setDeleteSent(false); setDeleteError(null); }}
              className="text-xs text-app-muted hover:text-crimson transition-colors sk-hit"
            >
              Delete account
            </button>
          </div>
        </section>

        {pendingPhoto && (
          <AvatarCropModal
            src={pendingPhoto}
            onSave={saveAvatar}
            onCancel={() => setPendingPhoto(null)}
          />
        )}

        {/* Delete account confirmation modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => !deleting && setShowDeleteModal(false)}>
            <div className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
              {deleteSent ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-green-600 dark:text-green-400">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-app-fg text-center mb-2">Check your email</h3>
                  <p className="text-sm text-app-muted text-center mb-2">
                    We sent a confirmation link to <span className="text-app-fg font-medium">{user?.email}</span>. Click the link to permanently delete your account. The link expires in 24 hours.
                  </p>
                  <p className="text-xs text-app-muted text-center mb-5 bg-app-bg rounded-lg px-3 py-2">
                    Can't find it? Check your <span className="text-app-fg font-medium">spam or junk folder</span>.
                  </p>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="w-full h-10 rounded-xl bg-app-card border border-app-border text-sm font-medium text-app-fg hover:bg-black/5 transition-colors sk-hit"
                  >
                    Close
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-app-fg mb-2">Delete your account?</h3>
                  <p className="text-sm text-app-muted mb-4 leading-relaxed">
                    This will permanently remove your account and all data — trips, conversations, portraits, payments. This cannot be undone.
                  </p>
                  <p className="text-sm text-app-fg mb-2">
                    Type <span className="font-mono font-semibold text-crimson">DELETE</span> to confirm:
                  </p>
                  <input
                    ref={deleteInputRef}
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    autoFocus
                    className="w-full h-10 px-3 rounded-xl border border-app-border bg-app-bg text-app-fg text-sm font-mono placeholder:text-app-muted/40 focus:outline-none focus:ring-2 focus:ring-crimson/30 mb-4"
                  />
                  {deleteError && <p className="text-xs text-crimson mb-3">{deleteError}</p>}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      disabled={deleting}
                      className="flex-1 h-10 rounded-xl bg-app-card border border-app-border text-sm font-medium text-app-fg hover:bg-black/5 transition-colors disabled:opacity-50 sk-hit"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => void handleDeleteAccount()}
                      disabled={deleteConfirmText !== 'DELETE' || deleting}
                      className="flex-1 h-10 rounded-xl bg-crimson text-white text-sm font-semibold hover:bg-crimson/90 transition-colors disabled:opacity-40 sk-hit"
                    >
                      {deleting ? <Spinner size="sm" className="text-white mx-auto" /> : 'Delete account'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;
